import { Suspense, lazy, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { OrganizationProvider, useOrganization } from "@/contexts/OrganizationContext";
import { UserPreferencesProvider } from "@/contexts/UserPreferencesContext";
import { OrganizationRoute } from "@/components/OrganizationRoute";

const queryClient = new QueryClient();

const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Transactions = lazy(() => import("./pages/Transactions"));
const Categories = lazy(() => import("./pages/Categories"));
const LoginPage = lazy(() => import("./features/auth/LoginPage"));
const ResetRequestPage = lazy(() => import("./features/auth/ResetRequestPage"));
const ResetCompletePage = lazy(() => import("./features/auth/ResetCompletePage"));
const WalletCards = lazy(() => import("./pages/cards/WalletCards"));
const Debts = lazy(() => import("./pages/Debts"));
const Goals = lazy(() => import("./pages/Goals"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Accounts = lazy(() => import("./pages/Accounts"));
const Expenses = lazy(() => import("./pages/cards/Expenses"));
const Invoices = lazy(() => import("./pages/cards/Invoices"));
const Reports = lazy(() => import("./pages/cards/Reports"));
const GeneralSettings = lazy(() => import("./pages/GeneralSettings"));
const OrganizationUsers = lazy(() => import("./pages/settings/OrganizationUsers"));
const Commitments = lazy(() => import("./pages/Commitments"));
const Imports = lazy(() => import("./pages/Imports"));
const Cofrinhos = lazy(() => import("./pages/Cofrinhos"));
const SharedReport = lazy(() => import("./pages/SharedReport"));
const Employees = lazy(() => import("./pages/Employees"));
const Payroll = lazy(() => import("./pages/Payroll"));
const EmployeeSettings = lazy(() => import("./pages/EmployeeSettings"));
const FinancialCalendar = lazy(() => import("./pages/FinancialCalendar"));
const RoutineCalendar = lazy(() => import("./pages/RoutineCalendar"));

function FullPageLoader({ label = "Carregando..." }: { label?: string }) {
  return <div className="min-h-screen flex items-center justify-center">{label}</div>;
}

function withSuspense(element: ReactNode) {
  return <Suspense fallback={<FullPageLoader />}>{element}</Suspense>;
}

function PublicRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <FullPageLoader />;
  }
  
  if (user) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
}

function OnboardingRoute({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const { organizations, isLoading: orgLoading } = useOrganization();

  if (authLoading || orgLoading) {
    return <FullPageLoader />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Usuário já tem organização — não precisa do onboarding
  if (organizations.length > 0) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <UserPreferencesProvider>
        <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <OrganizationProvider>
            <Routes>
              {/* Rotas públicas */}
              <Route path="/auth" element={withSuspense(<PublicRoute><LoginPage /></PublicRoute>)} />
              <Route path="/reset-request" element={withSuspense(<PublicRoute><ResetRequestPage /></PublicRoute>)} />
              <Route path="/reset-password" element={withSuspense(<ResetCompletePage />)} />
              <Route path="/shared/report/:shareId" element={withSuspense(<SharedReport />)} />

              {/* Onboarding (requer auth, bloqueia se já tem org) */}
              <Route path="/onboarding" element={withSuspense(<OnboardingRoute><Onboarding /></OnboardingRoute>)} />
              
              {/* Rotas com organização */}
              <Route path="/org/:orgSlug" element={withSuspense(<OrganizationRoute><Dashboard /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/transactions" element={withSuspense(<OrganizationRoute><Transactions /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/imports" element={withSuspense(<OrganizationRoute><Imports /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/categories" element={withSuspense(<OrganizationRoute><Categories /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/cofrinhos" element={withSuspense(<OrganizationRoute><Cofrinhos /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/calendar" element={withSuspense(<OrganizationRoute><FinancialCalendar /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/routine-calendar" element={withSuspense(<OrganizationRoute><RoutineCalendar /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/cards/walletcards" element={withSuspense(<OrganizationRoute><WalletCards /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/cards/expenses" element={withSuspense(<OrganizationRoute><Expenses /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/cards/invoices" element={withSuspense(<OrganizationRoute><Invoices /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/cards/reports" element={withSuspense(<OrganizationRoute><Reports /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/debts" element={withSuspense(<OrganizationRoute><Debts /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/goals" element={withSuspense(<OrganizationRoute><Goals /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/accounts" element={withSuspense(<OrganizationRoute><Accounts /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/commitments" element={withSuspense(<OrganizationRoute><Commitments /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/employees" element={withSuspense(<OrganizationRoute><Employees /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/employees/payroll" element={withSuspense(<OrganizationRoute><Payroll /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/employees/settings" element={withSuspense(<OrganizationRoute><EmployeeSettings /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/settings" element={withSuspense(<OrganizationRoute><GeneralSettings /></OrganizationRoute>)} />
              <Route path="/org/:orgSlug/settings/users" element={withSuspense(<OrganizationRoute><OrganizationUsers /></OrganizationRoute>)} />
              
              {/* Redirecionar raiz para organização */}
              <Route path="/" element={withSuspense(<OrganizationRoute><Dashboard /></OrganizationRoute>)} />
              <Route path="/transactions" element={withSuspense(<OrganizationRoute><Transactions /></OrganizationRoute>)} />
              <Route path="/imports" element={withSuspense(<OrganizationRoute><Imports /></OrganizationRoute>)} />
              <Route path="/categories" element={withSuspense(<OrganizationRoute><Categories /></OrganizationRoute>)} />
              <Route path="/cofrinhos" element={withSuspense(<OrganizationRoute><Cofrinhos /></OrganizationRoute>)} />
              <Route path="/calendar" element={withSuspense(<OrganizationRoute><FinancialCalendar /></OrganizationRoute>)} />
              <Route path="/routine-calendar" element={withSuspense(<OrganizationRoute><RoutineCalendar /></OrganizationRoute>)} />
              <Route path="/cards/walletcards" element={withSuspense(<OrganizationRoute><WalletCards /></OrganizationRoute>)} />
              <Route path="/cards/expenses" element={withSuspense(<OrganizationRoute><Expenses /></OrganizationRoute>)} />
              <Route path="/cards/invoices" element={withSuspense(<OrganizationRoute><Invoices /></OrganizationRoute>)} />
              <Route path="/cards/reports" element={withSuspense(<OrganizationRoute><Reports /></OrganizationRoute>)} />
              <Route path="/debts" element={withSuspense(<OrganizationRoute><Debts /></OrganizationRoute>)} />
              <Route path="/goals" element={withSuspense(<OrganizationRoute><Goals /></OrganizationRoute>)} />
              <Route path="/accounts" element={withSuspense(<OrganizationRoute><Accounts /></OrganizationRoute>)} />
              <Route path="/commitments" element={withSuspense(<OrganizationRoute><Commitments /></OrganizationRoute>)} />
              <Route path="/employees" element={withSuspense(<OrganizationRoute><Employees /></OrganizationRoute>)} />
              <Route path="/employees/payroll" element={withSuspense(<OrganizationRoute><Payroll /></OrganizationRoute>)} />
              <Route path="/employees/settings" element={withSuspense(<OrganizationRoute><EmployeeSettings /></OrganizationRoute>)} />
              <Route path="/settings" element={withSuspense(<OrganizationRoute><GeneralSettings /></OrganizationRoute>)} />
              <Route path="/settings/users" element={withSuspense(<OrganizationRoute><OrganizationUsers /></OrganizationRoute>)} />
              
              <Route path="*" element={withSuspense(<NotFound />)} />
            </Routes>
          </OrganizationProvider>
        </BrowserRouter>
      </TooltipProvider>
      </UserPreferencesProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
